// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what the "What Konvo knows" page does, shared by the desktop rows and the phone sheet:
// add with a toast for "already known" / "full", forget with Undo, move a note, open the VIPs.

import {M, TOPIC_DESCRIPTORS} from '@app/features/konvo_knows/components/KnowsMessages';
import KonvoKnows from '@app/features/konvo_knows/state/KonvoKnows';
import {displayTopic, KNOWS_TOPICS, type KnowsNote, type KnowsTopic} from '@app/features/konvo_knows/utils/KnowsModel';
import {ComponentBus} from '@app/features/platform/utils/ComponentBus';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {MenuGroupType, MenuRadioType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import type {I18n} from '@lingui/core';

/** Saves a new note. Resolves true when it was added (the box can clear). */
export async function addKnowsNote(i18n: I18n, text: string): Promise<boolean> {
	const result = await KonvoKnows.add(text);
	if (result.ok) return true;
	if (result.error === 'empty') return false;
	if (result.error === 'duplicate') ToastCommands.createToast({type: 'info', children: i18n._(M.duplicate)});
	else ToastCommands.error(i18n._(result.error === 'full' ? M.full : M.failed));
	return false;
}

/** Forgets a note at once; the page shows "Forgotten · Undo" where it was (KonvoKnows.recentlyForgotten). */
export async function forgetKnowsNote(i18n: I18n, note: KnowsNote): Promise<void> {
	if (!(await KonvoKnows.forget(note.id))) ToastCommands.error(i18n._(M.failed));
}

/** Undo of a forget: the same text back in the same topic, with its source. */
export async function undoForgetKnowsNote(i18n: I18n, note: KnowsNote): Promise<void> {
	const result = await KonvoKnows.restore(note);
	if (!result.ok && result.error !== 'duplicate') ToastCommands.error(i18n._(M.failed));
}

export async function updateKnowsNote(
	i18n: I18n,
	note: KnowsNote,
	change: {text?: string; topic?: KnowsTopic | null},
): Promise<boolean> {
	const text = change.text?.trim();
	const textChanged = text !== undefined && text !== note.text;
	const topicChanged = change.topic !== undefined && change.topic !== displayTopic(note);
	if (!textChanged && !topicChanged) return true;
	const ok = await KonvoKnows.update(note.id, {
		text: textChanged ? text : undefined,
		topic: topicChanged ? change.topic : undefined,
	});
	if (!ok) ToastCommands.error(i18n._(M.failed));
	return ok;
}

/** The topics as radio items for a "Move to" menu. */
export function topicRadios(
	i18n: I18n,
	current: KnowsTopic,
	onSelect: (topic: KnowsTopic) => void,
): Array<MenuRadioType> {
	return KNOWS_TOPICS.map((topic) => ({
		label: i18n._(TOPIC_DESCRIPTORS[topic]),
		selected: topic === current,
		onSelect: () => onSelect(topic),
	}));
}

/** The menu of a note row (right click on desktop): Change, Move to, Forget. */
export function noteMenuGroups(
	i18n: I18n,
	note: KnowsNote,
	actions: {onChange: () => void; close: () => void},
	icons: {change: React.ReactNode; move: React.ReactNode; forget: React.ReactNode},
): Array<MenuGroupType> {
	return [
		{
			items: [
				{
					id: 'change',
					icon: icons.change,
					label: i18n._(M.change),
					onClick: () => {
						actions.close();
						actions.onChange();
					},
				},
				{
					id: 'move',
					icon: icons.move,
					label: i18n._(M.moveTo),
					items: topicRadios(i18n, displayTopic(note), (topic) => {
						actions.close();
						void updateKnowsNote(i18n, note, {topic});
					}),
				},
			],
		},
		{
			items: [
				{
					id: 'forget',
					icon: icons.forget,
					label: i18n._(M.forget),
					danger: true,
					onClick: () => {
						actions.close();
						void forgetKnowsNote(i18n, note);
					},
				},
			],
		},
	];
}

/** "Edit" on the VIPs row: the Assistant tab's Proactive section, where the VIPs live. */
export function openVipSettings(): void {
	ComponentBus.dispatch('USER_SETTINGS_TAB_SELECT', {tab: 'konvo_assistant', section: 'konvo-assistant-proactive'});
}

/** The Assistant tab's link row: open this page. */
export function openKnowsTab(): void {
	ComponentBus.dispatch('USER_SETTINGS_TAB_SELECT', {tab: 'assistant_knows'});
}
